import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  User,
  Button,
  Image,
  Divider,
} from '@nextui-org/react';
import { GoComment } from 'react-icons/go';
import { MdRocketLaunch } from 'react-icons/md';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { decryptData } from '../../utils/encryptData';
import { formatName } from '../../utils/nameFormatter';
import CommentReplyBox from '../CommentReplyBox/CommentReplyBox';
import PostDropdown from '../PostDropdown/PostDropdown';
import { message } from 'antd';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import { deleteComment } from '../../services/querys ';

function Comment({ comment, level, onReply }) {
  const [isReplyBoxOpen, setIsReplyBoxOpen] = useState(false);
  const { uid, rl } = useSelector((state) => state.auth);
  const loggedInUserId = decryptData(uid);
  const role = decryptData(rl);

  const queryClient = useQueryClient();

  const handleReplyClick = () => {
    setIsReplyBoxOpen((prev) => !prev);
  };

  const { mutate: deleteCommentMutation, isLoading: isDeleteComment } =
    useMutation({
      mutationFn: deleteComment,
      onError: (error) => {
        console.error('Error delete comment:', error);
        message.error(error?.response?.data?.error || error.message);
      },
      onSuccess: async () => {
        message.success('Comment delete successfully');
        await queryClient.invalidateQueries(['posts']);
      },
    });

  const handelDelete = (id) => {
    if (!id) {
      console.error('Comment id not found!');
      return;
    }
    deleteCommentMutation(id);
  };

  return (
    <div style={{ paddingLeft: level * 5 + 'px' }} className="comment-box">
      <Card
        disableRipple={true}
        shadow="none"
        className="mx-2 md:mx-5 font-poppins bg-transparent border-transparent"
      >
        <CardHeader>
          <User
            name={
              comment?.userName
                ? formatName(comment?.userName).shortname
                : 'Unknown User'
            }
            description={moment(comment?.createdAt).fromNow()}
            avatarProps={{
              name: comment?.userName
                ? formatName(comment?.userName).lastName
                : 'Unknown',
            }}
            showFallback
            src="https://images.unsplash.com/broken"
            className="text-gray-300 text-md font-poppins"
          />
        </CardHeader>
        <CardBody className="-mt-1">
          <p className="text-start text-gray-300 text-xs">
            {comment?.description || 'No description'}.
          </p>
          {comment?.image[0]?.url && (
            <Image
              loading="lazy"
              alt="Comment Image"
              src={comment?.image[0]?.url}
              className="mt-4"
            />
          )}
        </CardBody>
        <CardFooter className="flex justify-between gap-6 -mt-2">
          <div className="flex justify-between md:justify-start gap-6">
            <Button
              startContent={<MdRocketLaunch size={20} />}
              className="bg-stone-700 text-gray-200 justify-start text-xs bg-transparent border-none"
              size="sm"
            >
              {comment?.likes || 0}
            </Button>
            <Button
              onClick={handleReplyClick}
              startContent={<GoComment size={20} />}
              className="bg-stone-700 text-gray-200 justify-start text-xs bg-transparent border-none"
              size="sm"
              // disableRipple={true}
            >
              Reply
            </Button>
          </div>

          {/* Options for post owner */}
          {(comment?.userId === loggedInUserId || role === 'admin') && (
            <PostDropdown deleteItem={() => handelDelete(comment?.commentId)} />
          )}
        </CardFooter>
      </Card>

      <Divider className="mx-10 bg-gray-600 justify-center" />

      {/* Reply Box */}
      {isReplyBoxOpen && (
        <div className="ml-3">
          <CommentReplyBox
            onPress={() => setIsReplyBoxOpen(false)}
            onSubmit={(commentText, file) => {
              onReply(comment?.commentId, commentText, file, comment?.postId);
              setIsReplyBoxOpen(false);
            }}
          />
        </div>
      )}

      {/* Render nested replies */}
      {comment.replies && comment?.replies?.length > 0 && (
        <div style={{ paddingLeft: '5px' }}>
          {comment?.replies.map((reply) => (
            <Comment
              key={reply._id}
              comment={reply}
              level={level + 1}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Comment;
